import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { deleteMyAccount, getDeletionCheck } from "@/lib/api";
import { disableWebPush } from "@/lib/pushNotifications";
import { clearUser } from "@/lib/storage";

// Shown in Settings › Account and on /delete-account. Keep in step with the privacy policy and the app.
export const DELETED_DATA = [
  "Your profile, goals, photos and sign-in details (email, password, Google or GitHub link)",
  "Tasks, streaks, check-ins, focus sessions and Flex stamps",
  "Matches, likes, partnerships, shared workspaces and every chat message you sent",
  "Cohort seats and partner invites",
  "Shield rules, devices and the site and app time they recorded",
  "Notifications, push tokens and your token wallet",
];
export const KEPT_DATA = [
  "Records of completed payments (amount, date, plan, payment reference) with your name and email removed, for refunds, disputes and tax. Razorpay keeps its own payment records under its policy.",
  "If you sent a deletion request from the website, the request itself (email and dates) as proof it was handled.",
];

export function DeletionLists() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="border border-white/10 p-5">
        <div className="overline mb-3">Deleted right away</div>
        <ul className="space-y-2 text-sm leading-relaxed text-[#B7B7BD]">{DELETED_DATA.map((item) => <li key={item} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 bg-[#F59E0B]" />{item}</li>)}</ul>
      </div>
      <div className="border border-white/10 p-5">
        <div className="overline mb-3">Kept</div>
        <ul className="space-y-2 text-sm leading-relaxed text-[#B7B7BD]">{KEPT_DATA.map((item) => <li key={item} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 bg-white/40" />{item}</li>)}</ul>
        <p className="mt-4 text-xs leading-relaxed text-[#777]">Your partners lose the shared workspace and are told the partnership ended. Paid plan time left is not refunded.</p>
      </div>
    </div>
  );
}

export default function DeleteAccountPanel() {
  const navigate = useNavigate();
  const [check, setCheck] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDeletionCheck().then(setCheck).catch((error) => toast.error(error.message));
  }, []);

  const word = check?.confirmationWord || "DELETE";
  const ready = confirmation.trim().toUpperCase() === word && (!check?.needsPassword || password.length > 0);

  const submit = async (event) => {
    event.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      await deleteMyAccount({ confirmation, password });
      try { await disableWebPush(); } catch { /* The account is gone either way. */ }
      clearUser();
      navigate("/delete-account?deleted=1", { replace: true });
    } catch (error) {
      toast.error(error.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="delete-account-panel" className="border border-[#F87171]/30 bg-[#F87171]/[0.03] p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-[#F87171]" />
        <div>
          <div className="font-serif-editorial text-2xl">Delete this account</div>
          <p className="mt-1 text-sm text-[#A1A1AA]">
            {check ? <>This permanently deletes <span className="text-[#F5F5F0]">{check.email}</span> and can't be undone.</> : "This permanently deletes your account and can't be undone."}
          </p>
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2 text-xs text-[#A1A1AA]">Type {word} to confirm
          <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" spellCheck={false} data-testid="delete-confirmation" className="border border-white/10 bg-black px-3 py-2.5 text-sm uppercase tracking-[0.12em] text-white outline-none focus:border-[#F87171]/60" />
        </label>
        {check?.needsPassword && (
          <label className="grid gap-2 text-xs text-[#A1A1AA]">Your password
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" className="border border-white/10 bg-black px-3 py-2.5 text-sm text-white outline-none focus:border-[#F87171]/60" />
          </label>
        )}
      </div>
      <button type="submit" disabled={!ready || busy} data-testid="delete-account-submit" className="mt-5 bg-[#F87171] px-5 py-3 text-xs font-semibold text-black transition-opacity disabled:cursor-not-allowed disabled:opacity-40">
        {busy ? "Deleting…" : "Delete my account permanently"}
      </button>
    </form>
  );
}
